import { useState, useEffect, useMemo } from "react";
import { Country, City } from "country-state-city";
import { apiRequest } from "@services/apiClient";
import { COMMON_TIMEZONES } from "./timezones";
import { inputBase, labelBase } from "./styles";
import AgentQuestionsEditor from "@components/admin-clinics/AgentQuestionsEditor";

export default function ClinicSection({
  form,
  onChange,
  ALL_TIMEZONES,
  purposes = [],
  questionLibrary = [],
  questionLibraryLoading = false,
  agentQuestionsByPurpose,
  setAgentQuestionsByPurpose,
}) {
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const countries = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        code: c.isoCode,
        name: c.name,
      })),
    []
  );

  const citySuggestions = useMemo(() => {
    if (!form.clinic_country) return [];
    const cities = City.getCitiesOfCountry(form.clinic_country) || [];
    return cities.map((c) => c.name);
  }, [form.clinic_country]);

  useEffect(() => {
    let cancelled = false;
    setLoadingAgents(true);
    apiRequest({ method: "GET", endpoint: "/clinic/elevenlabs-agents" })
      .then((res) => {
        if (!cancelled) setAgents(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setAgents([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAgents(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="rounded-3xl border border-slate-200 bg-slate-50/60 p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Clinic</h2>
        <span className="text-xs rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-700">
          Stored first
        </span>
      </div>

      <div className="mt-5 grid gap-4">
        <div className="grid gap-2">
          <label className={labelBase}>Clinic name</label>
          <input
            name="clinic_name"
            required
            className={inputBase}
            value={form.clinic_name}
            onChange={onChange}
            placeholder="Klinifai Dental"
          />
        </div>

        <div className="grid gap-2">
          <label className={labelBase}>Address</label>
          <input
            name="clinic_address"
            className={inputBase}
            value={form.clinic_address}
            onChange={onChange}
            placeholder="Street, City, Postcode"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="grid gap-2">
            <label className={labelBase}>Clinic phone</label>
            <input
              name="clinic_phone"
              className={inputBase}
              value={form.clinic_phone}
              onChange={onChange}
              placeholder="+31 ..."
            />
          </div>

          <div className="grid gap-2">
            <label className={labelBase}>Country</label>
            <select
              name="clinic_country"
              className={inputBase}
              value={form.clinic_country}
              onChange={onChange}
            >
              <option value="">
                Select a country
              </option>
              {countries.map(({ code, name }) => (
                <option key={code} value={code}>
                  {name} ({code})
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-2">
            <label className={labelBase}>City</label>
            <input
              name="clinic_city"
              className={inputBase}
              value={form.clinic_city}
              onChange={onChange}
              placeholder={form.clinic_country ? "e.g. Amsterdam, New York" : "Select a country first"}
              list="city-suggestions"
              autoComplete="off"
              disabled={!form.clinic_country}
            />
            <datalist id="city-suggestions">
              {citySuggestions.map((city) => (
                <option key={city} value={city} />
              ))}
            </datalist>
          </div>

          <div className="grid gap-2">
            <label className={labelBase}>Preferred timezone</label>
            <select
              name="preferred_timezone"
              className={inputBase}
              value={form.preferred_timezone}
              onChange={onChange}
            >
              <optgroup label="Common">
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </optgroup>

              <optgroup label="All timezones">
                {ALL_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>

        <div className="grid gap-2">
          <label className={labelBase}>Clinic info</label>
          <input
            name="clinic_info"
            className={inputBase}
            value={form.clinic_info}
            onChange={onChange}
            placeholder="Optional notes"
          />
        </div>

        <div className="grid gap-2">
          <label className={labelBase}>ElevenLabs Agent</label>
          <select
            name="elevenlabs_agent_id"
            required
            className={inputBase}
            value={form.elevenlabs_agent_id}
            onChange={onChange}
            disabled={loadingAgents}
          >
            <option value="">
              {loadingAgents ? "Loading agents…" : "Select an agent"}
            </option>
            {agents.map(({ agent_id, name }) => (
              <option key={agent_id} value={agent_id}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-2">
          <label className={labelBase}>Clinic agent name</label>
          <input
            name="clinic_agent_name"
            className={inputBase}
            value={form.clinic_agent_name}
            onChange={onChange}
            placeholder="(Optional)"
          />
        </div>

        <div className="grid gap-2">
          <label className={labelBase}>Welcome message</label>
          <textarea
            name="welcome_message"
            className={`${inputBase} min-h-[120px] py-3`}
            value={form.welcome_message}
            onChange={onChange}
            placeholder={`This call may be recorded for quality purposes. Thank you for calling ${
              form.clinic_name || "our clinic"
            }. How may I help you today?`}
          />
          <div className="text-xs text-slate-500">
            This is the first thing the AI says when answering calls. You can
            include multiple languages.
          </div>
        </div>

        <div className="grid gap-2">
          {questionLibraryLoading ? (
            <div className="text-sm text-slate-600">
              <label className={labelBase}>Agent Questions</label>
              Loading question library…
            </div>
          ) : (
            <AgentQuestionsEditor
              value={agentQuestionsByPurpose}
              onChange={setAgentQuestionsByPurpose}
              questionLibrary={questionLibrary}
              purposes={purposes}
            />
          )}
        </div>
      </div>
    </section>
  );
}
